import { uid } from '@runtime/shared/utils/id'
import {
  computeLevelFromRating,
  makeDefaultUser,
  type UserProfile,
} from '@runtime/core/user/userModel'
import { normalizeUserRole } from '@runtime/core/user/userRole'
import type {
  AppStoreState,
  UserServerPayload,
} from '@runtime/core/store/appStoreTypes'
import type { AppStoreSlice } from '@runtime/core/store/slices/sliceTypes'
import {
  pickInitialAuthToken,
  pickInitialUser,
} from '@runtime/core/store/appStorePersist'

type UserSlice = Pick<
  AppStoreState,
  | 'user'
  | 'authToken'
  | 'setAuthToken'
  | 'setUserFromServer'
  | 'updateProfile'
  | 'logout'
>

export const createUserSlice: AppStoreSlice<UserSlice> = (set) => ({
  user: pickInitialUser(),
  authToken: pickInitialAuthToken(),

  setAuthToken: (token: string | null) => {
    const next = token ? String(token) : null
    set({ authToken: next })
  },

  setUserFromServer: (payload?: UserServerPayload | null) => {
    if (!payload) return
    set((s) => {
      const sameAccount = !payload.id || String(payload.id) === s.user?.id
      const base = s.user && sameAccount ? { ...s.user } : makeDefaultUser()
      const pending = s.gameResultsOutbox[String(payload.id ?? base.id)]?.length
      const preserveStats = sameAccount && pending
      const next: UserProfile = {
        ...base,
        id: payload.id != null ? String(payload.id) : base.id || uid(),
        pseudo: payload.pseudo ?? base.pseudo,
        email: payload.email ?? base.email,
        role: normalizeUserRole(payload.role) ?? base.role,
        rating: preserveStats
          ? base.rating
          : (payload.rating ?? base.rating ?? 1200),
        games: preserveStats ? base.games : (payload.games ?? base.games),
      }
      next.level = computeLevelFromRating(next.rating)
      return {
        user: next,
        onlineNick: next.pseudo ? String(next.pseudo) : s.onlineNick,
      }
    })
  },

  updateProfile: (partial: Partial<UserProfile>) => {
    set((s) => {
      if (!s.user) return {}
      const base = { ...s.user }
      const next = { ...base, ...partial }
      next.rating = next.rating || base.rating || 1200
      next.level = computeLevelFromRating(next.rating)
      return { user: next }
    })
  },

  logout: (options) => {
    set((s) => {
      const gameResultsOutbox = { ...s.gameResultsOutbox }
      if (options?.discardPendingResults && s.user)
        delete gameResultsOutbox[s.user.id]
      return { user: null, authToken: null, gameResultsOutbox }
    })
  },
})
